import React, { useEffect, useRef } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { styled, useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Button,
    Grid,
    Paper,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Mention, MentionsInput } from 'react-mentions';

import { parseDate, formatDate } from 'common/utils/dates';
import { truncateString } from 'common/utils/helpers';

import mentionsMessageStyle from './mentionsMessageStyle';
import mentionStyle from './mentionStyle';

// Estilos
const ScrollableContent = styled('div')`
    flex-grow: 1;
    margin-bottom: 15px;
    min-height: 0;
    overflow: auto;
`;

const Comment = styled('div')`
    border-radius: 3px;
    display: flex;
    gap: 1rem;
    margin-bottom: 5px;
    padding: 5px;
    font-size: 14px;
`;

const MessagesList = ({ messages, updateMessage, users }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const messagesEndRef = useRef(null);

    // Desplazar al final cuando cambian los mensajes
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTo(
                0,
                messagesEndRef.current.scrollHeight,
            );
        }
    };

    const renderDate = (comment) => {
        if (comment.created_at) {
            return formatDate(
                parseDate(comment.created_at, 'dd/MM/yyyy HH:mm:ss'),
                'dd/MM/yyyy hh:mm:ss',
            );
        }
        return get(comment, 'created_at', '');
    };

    const data = users.map((user) => ({
        display: `${user.first_name} ${user.last_name}`,
        id: user.id,
    }));

    return (
        <ScrollableContent ref={messagesEndRef}>
            {messages.map((comment) => (
                <Paper
                    key={`comment-${comment.id}`}
                    sx={{
                        backgroundColor: comment.is_comment ? '#fdf6d9' : '',
                        color: comment.is_comment ? '#000' : '',
                    }}
                >
                    <Comment>
                        <Avatar alt="" src={comment.image} />
                        <Box sx={{ width: '100%' }}>
                            <div className="role-name">
                                <Grid container>
                                    <Grid size={{ xs: 'grow' }}>
                                        <Typography
                                            fontWeight="bold"
                                            variant="body2"
                                        >
                                            {comment.username} -{' '}
                                            <small>
                                                ({comment.name} - {comment.role}
                                                )
                                            </small>
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <small>{renderDate(comment)}</small>
                                    </Grid>
                                </Grid>
                            </div>
                            <MentionsInput
                                style={{
                                    ...mentionsMessageStyle,
                                    input: {
                                        ...mentionsMessageStyle.input,
                                        color: !comment.is_comment
                                            ? isDarkMode
                                                ? '#fff'
                                                : '#000'
                                            : '#000',
                                    },
                                }}
                                value={truncateString(
                                    comment.message,
                                    comment?.maxCharacters || 250,
                                )}
                            >
                                <Mention
                                    data={data}
                                    style={
                                        isDarkMode && !comment.is_comment
                                            ? {
                                                  backgroundColor:
                                                      'rgba(255, 255, 255, 0.09)',
                                              }
                                            : mentionStyle
                                    }
                                />
                            </MentionsInput>
                            <Box
                                hidden={comment?.message?.length <= 250}
                                textAlign="center"
                            >
                                <Button
                                    color="secondary"
                                    variant="text"
                                    onClick={() =>
                                        updateMessage({
                                            commentId: comment.id,
                                            maxCharacters:
                                                comment?.maxCharacters > 250
                                                    ? 250
                                                    : comment.message.length,
                                        })
                                    }
                                    sx={{ p: 0 }}
                                >
                                    {comment?.maxCharacters > 250 ? (
                                        <ExpandLess title={t('View less')} />
                                    ) : (
                                        <ExpandMore title={t('View more')} />
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Comment>
                </Paper>
            ))}
        </ScrollableContent>
    );
};

MessagesList.propTypes = {
    messages: PropTypes.array.isRequired,
    updateMessage: PropTypes.func.isRequired,
    users: PropTypes.array.isRequired,
};

export default MessagesList;
